import React, { useState } from "react"
import { useMutation } from "urql"

import PaymentProviderWrapper from "../../components/payments/PaymentProviderWrapper"
import { Button } from "../../components/shared/Buttons"
import { Banner } from "../../components/shared/Layout"
import { AnimatedModal } from "../../components/shared/Modal"
import Typography from "../../components/shared/Typography"
import { usePractice } from "../../contexts/PracticeContext"
import { useToast } from "../../contexts/ToastContext"
import { formatPrice } from "../../utils/utils"

import BookableEventPaymentModal from "./BookableEventPaymentModal"
import { resolvePaymentRequestSendTo } from "./bookableEventPaymentStatus"
import {
  PROCESS_REGISTRATION_PAYMENT_MUTATION,
  PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION,
  REQUEST_EVENT_REGISTRATION_PAYMENT_MUTATION,
  REQUEST_EVENT_SERIES_REGISTRATION_PAYMENT_MUTATION
} from "./mutations"

export default function PostAddRegistrationPaymentWizard({
  visible,
  hideModal,
  registration,
  isSeries = false,
  onCompleted
}) {
  const { practice } = usePractice()
  const { showToast } = useToast()
  const [requesting, setRequesting] = useState(false)
  const [error, setError] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)

  const requestMutation = isSeries
    ? REQUEST_EVENT_SERIES_REGISTRATION_PAYMENT_MUTATION
    : REQUEST_EVENT_REGISTRATION_PAYMENT_MUTATION
  const requestField = isSeries ? "requestEventSeriesRegistrationPayment" : "requestEventRegistrationPayment"
  const [, requestPayment] = useMutation(requestMutation)

  const client = registration?.client
  const amountLabel = formatPrice(registration?.amountCents || 0, practice?.defaultCurrency, {
    alwaysShowDecimals: true
  })

  const finish = () => {
    hideModal()
    onCompleted()
  }

  const handleRequestPayment = async () => {
    if (!registration?.id) return

    const sendTo = resolvePaymentRequestSendTo(client)
    if (!sendTo) {
      setError("Client has no email or phone on file")
      return
    }

    setRequesting(true)
    setError(null)

    try {
      const result = await requestPayment({
        registrationId: registration.id,
        sendTo
      })

      if (result.data?.[requestField]?.result === "success") {
        showToast("Payment request sent")
        finish()
      } else {
        setError(result.data?.[requestField]?.errors?.[0] || "Failed to send payment request")
      }
    } catch (err) {
      setError(err.message || "Failed to send payment request")
    } finally {
      setRequesting(false)
    }
  }

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false)
    showToast("Payment processed")
    finish()
  }

  return (
    <>
      <AnimatedModal visible={visible} hideModal={finish} header="Collect payment?">
        <div className="space-y-4 py-2">
          <Typography variant="body">
            {client?.firstName} {client?.lastName} was added with amount <strong>{amountLabel || "$0.00"}</strong>.
            Adding a client does not charge them automatically.
          </Typography>

          <Banner type="info">
            Request payment sends a link so they can pay. Process payment charges a card on file (or a new card) now.
          </Banner>

          {error && <div className="text-sm text-red">{error}</div>}

          <div className="flex flex-col gap-2">
            <Button type="primary" className="w-full" onClick={handleRequestPayment} disabled={requesting}>
              {requesting ? "Sending..." : "Request payment"}
            </Button>
            {practice?.hasPaymentProvider ? (
              <Button
                type="secondary"
                className="w-full"
                onClick={() => setShowPaymentModal(true)}
                disabled={requesting}>
                Process payment
              </Button>
            ) : (
              <div className="rounded bg-gray-ultralight p-3 text-sm text-gray-dark">
                Connect a payment provider to charge cards in Heal.me.{" "}
                <a href="/portal/settings/banking" className="text-teal underline">
                  Connect payment provider
                </a>
              </div>
            )}
            <Button type="tertiary" className="w-full" onClick={finish} disabled={requesting}>
              Skip for now
            </Button>
          </div>
        </div>
      </AnimatedModal>

      {practice?.hasPaymentProvider && registration && client && (
        <PaymentProviderWrapper practice={practice} client={client}>
          <BookableEventPaymentModal
            visible={showPaymentModal}
            hideModal={() => setShowPaymentModal(false)}
            client={client}
            registration={registration}
            onPaymentSuccess={handlePaymentSuccess}
            processPaymentMutation={
              isSeries ? PROCESS_SERIES_REGISTRATION_PAYMENT_MUTATION : PROCESS_REGISTRATION_PAYMENT_MUTATION
            }
            processPaymentField={isSeries ? "processSeriesRegistrationPayment" : "processRegistrationPayment"}
          />
        </PaymentProviderWrapper>
      )}
    </>
  )
}
